import React, { useState, useEffect, useMemo } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle } from "lucide-react";
import useAuth from "../hooks/useAuth";
import api from "../services/api";

const PERIOD_OPTIONS = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

// Columns for the sortable leaderboard table. `direction` on default is the
// preferred sort (desc for volume/rates-where-higher-is-good, asc for latency
// and opt-out where lower is better).
const COLUMNS = [
  { key: "name", label: "Texter", align: "left" },
  { key: "messages_sent", label: "Sent", align: "right", defaultDir: "desc", fmt: (v) => v.toLocaleString() },
  { key: "reply_to_send_latency_sec", label: "Reply → Send", align: "right", defaultDir: "asc", fmt: fmtSeconds, subtitle: "median" },
  { key: "send_to_reply_latency_sec", label: "Send → Reply", align: "right", defaultDir: "asc", fmt: fmtSeconds, subtitle: "median" },
  { key: "opt_out_rate", label: "Opt-out", align: "right", defaultDir: "asc", fmt: fmtPct, subtitle: "of contacts" },
  { key: "tag_completion_rate", label: "Tag rate", align: "right", defaultDir: "desc", fmt: fmtPct, subtitle: "of replies" },
  { key: "avg_hold_duration_ms", label: "Avg hold", align: "right", defaultDir: "desc", fmt: (v) => `${Math.round(v)}ms` },
];

function fmtSeconds(v) {
  if (v == null) return "—";
  if (v < 60) return `${Math.round(v)}s`;
  if (v < 3600) return `${Math.round(v / 60)}m`;
  return `${(v / 3600).toFixed(1)}h`;
}

function fmtPct(v) {
  if (v == null) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

export default function TexterDashboard() {
  const { user } = useAuth();
  const [texters, setTexters] = useState([]);
  const [period, setPeriod] = useState(30);
  const [sortKey, setSortKey] = useState("messages_sent");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/performance/texters", { params: { period_days: period } })
      .then((res) => setTexters(res.data.texters || []))
      .catch(() => setTexters([]))
      .finally(() => setLoading(false));
  }, [period]);

  const sorted = useMemo(() => {
    const arr = [...texters];
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      // Nulls always sort last regardless of direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [texters, sortKey, sortDir]);

  const handleSort = (key, defaultDir) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(defaultDir || "desc");
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Texter Performance</h1>
        <select
          value={period}
          onChange={(e) => setPeriod(parseInt(e.target.value, 10))}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
        >
          {PERIOD_OPTIONS.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-400">Loading performance data...</div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {COLUMNS.map((col) => {
                  const isActive = sortKey === col.key;
                  const ArrowIcon = !isActive
                    ? ArrowUpDown
                    : sortDir === "asc"
                      ? ArrowUp
                      : ArrowDown;
                  return (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key, col.defaultDir)}
                      className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none text-${col.align}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        <ArrowIcon className={`w-3 h-3 ${isActive ? "text-gray-700" : "text-gray-400"}`} />
                      </span>
                      {col.subtitle && (
                        <div className="text-[10px] font-normal text-gray-400 normal-case">{col.subtitle}</div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-4 py-6 text-center text-gray-400 text-sm">
                    No texter data for this period.
                  </td>
                </tr>
              ) : (
                sorted.map((t, idx) => (
                  <tr key={t.user_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        {idx < 3 && sortKey === "messages_sent" && sortDir === "desc" && (
                          <span className="text-xs font-bold text-amber-500">#{idx + 1}</span>
                        )}
                        <span className="font-medium">{t.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right font-mono">
                      {t.messages_sent.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right font-mono">
                      {fmtSeconds(t.reply_to_send_latency_sec)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right font-mono">
                      {fmtSeconds(t.send_to_reply_latency_sec)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-mono">
                      <span className={t.opt_out_rate > 0.05 ? "text-red-600 font-medium" : "text-gray-600"}>
                        {fmtPct(t.opt_out_rate)}
                      </span>
                      {t.opt_out_rate > 0.05 && (
                        <AlertTriangle className="w-3 h-3 inline ml-1 text-red-500" />
                      )}
                      <div className="text-[10px] text-gray-400">
                        {t.contacts_opted_out}/{t.contacts_sent}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-mono">
                      <span className={t.tag_completion_rate < 0.5 && t.replies_sent_to >= 5 ? "text-amber-600 font-medium" : "text-gray-600"}>
                        {fmtPct(t.tag_completion_rate)}
                      </span>
                      <div className="text-[10px] text-gray-400">
                        {t.replies_tagged}/{t.replies_sent_to}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right font-mono">
                      {Math.round(t.avg_hold_duration_ms)}ms
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div className="text-xs text-gray-500 space-y-1">
        <p>
          <strong>Reply → Send:</strong> median time from an inbound reply to the texter's next outbound on that conversation (capped at 24h).{" "}
          <strong>Send → Reply:</strong> median time from the texter's send to the contact's first reply.
        </p>
        <p>
          <strong>Opt-out:</strong> fraction of unique contacts the texter sent to whose campaign_contact is now OPTED_OUT. Red flag above 5%.{" "}
          <strong>Tag rate:</strong> fraction of replied contacts the texter sent to that they also tagged. Amber flag below 50% with ≥5 eligible.
        </p>
      </div>
    </div>
  );
}
